import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, XCircle, Loader2, Eye, EyeOff } from "lucide-react";
import axios from "axios";
import { fadeInUp, transition } from "@/utils/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [status, setStatus] = useState(token ? "form" : "error");
  const [message, setMessage] = useState(token ? "" : "No reset token provided.");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/reset-password`, { token, password });
      setStatus("success");
      setMessage(res.data.message);
    } catch (err) {
      setStatus("error");
      setMessage(err.response?.data?.detail || "Reset failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-24" data-testid="reset-password-page">
      <motion.div {...fadeInUp} transition={transition} className="hud-card clip-corners p-8 text-center">
        {status === "form" && (
          <form onSubmit={handleSubmit} data-testid="reset-form">
            <h1 className="font-headings text-2xl uppercase tracking-widest text-[#FFE800] mb-2">
              Reset Password
            </h1>
            <p className="text-sm text-gray-400 mb-6">Choose a new password for your account.</p>
            <div className="relative text-left">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="New password (min 8 characters)"
                className="w-full bg-black/50 border border-white/20 text-white font-data text-sm py-3 px-4 pr-10 focus:border-[#FFE800] outline-none"
                data-testid="reset-password-input"
                required
                minLength={8}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-[#FFE800]"
                data-testid="reset-toggle-password"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <button
              type="submit"
              disabled={loading || password.length < 8}
              className="mt-6 w-full bg-[#FFE800] text-black font-headings uppercase tracking-widest py-3 hover:bg-[#FFD700] disabled:opacity-40"
              data-testid="reset-submit"
            >
              {loading ? "Resetting..." : "Set New Password"}
            </button>
          </form>
        )}
        {status === "success" && (
          <div data-testid="reset-success">
            <CheckCircle className="h-12 w-12 text-green-400 mx-auto mb-4" />
            <h1 className="font-headings text-2xl uppercase tracking-widest text-green-400 mb-2">
              Password Reset
            </h1>
            <p className="text-sm text-gray-400 mb-6">{message}</p>
            <Link
              to="/login"
              className="inline-block bg-[#FFE800] text-black font-headings uppercase tracking-widest py-3 px-8 hover:bg-[#FFD700]"
              data-testid="reset-login-link"
            >
              Sign In
            </Link>
          </div>
        )}
        {status === "error" && (
          <div data-testid="reset-error">
            <XCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
            <h1 className="font-headings text-2xl uppercase tracking-widest text-red-400 mb-2">
              Reset Failed
            </h1>
            <p className="text-sm text-gray-400 mb-6">{message}</p>
            <Link
              to="/login"
              className="inline-block border border-white/20 text-white font-headings uppercase tracking-widest py-3 px-8 hover:border-[#FFE800]"
              data-testid="reset-back-link"
            >
              Back to Login
            </Link>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default ResetPassword;
